import AutoResizeTextArea from "atoms/forms/auto-resize-text-area";
import { ImageAnnotationRecord } from "models/view-models/block-configurations/image-annotation-record";
import { ModalTypes } from "molecules/constants/modal-types";
import BaseModalFooter from "molecules/modals/footers/base-modal-footer";
import BaseModalHeader from "molecules/modals/headers/base-modal-header";
import Modal from "molecules/modals/modal";
import React, { useEffect, useState } from "react";
import useImageAnnotations from "utilities/hooks/domain/notebooks/pages/use-image-annotations";
import { t } from "utilities/localization-utils";

interface ImageAnnotationsModalProps {
    imageAnnotation?: ImageAnnotationRecord;
    number?: number;
    onClose: () => void;
    onCancel: () => void;
    onSubmit?: (
        updatedAnnotation: ImageAnnotationRecord | null | undefined
    ) => Promise<void>;
    isVisible: boolean;
}
const CSS_CLASS_NAME = "c-image-annotations-modal";
const IMAGE_ANNOTATION_NOTE_ID = "image-annotation-note-field";
const MAX_NOTE_LENGTH = 300;
const ImageAnnotationsModal = (props: ImageAnnotationsModalProps) => {
    const { onClose, onSubmit, isVisible, imageAnnotation, number = 0 } = props;
    const [draftNote, setDraftNote] = useState("");
    const { createImageAnnotation, updateImageAnnotation } =
        useImageAnnotations();

    const modalHeading = `${t(imageAnnotation?.id ? "editItem" : "addItem", {
        item: t("annotation"),
    })} ${number}`;

    const handleOnNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDraftNote(e.target.value);
    };

    const createOrUpdateImageAnnotation = async () => {
        if (imageAnnotation) {
            const annotationToCreateOrUpdate = imageAnnotation?.with({
                note: draftNote,
            });
            return imageAnnotation.id
                ? await updateImageAnnotation(annotationToCreateOrUpdate)
                : await createImageAnnotation(annotationToCreateOrUpdate);
        }
    };

    const handleSubmitClick = async () => {
        const response = await createOrUpdateImageAnnotation();
        onSubmit?.(response);
        handleClose();
    };

    const handleClose = () => {
        setDraftNote(imageAnnotation?.note ?? "");
        onClose();
    };

    useEffect(() => {
        setDraftNote(imageAnnotation?.note ?? "");
    }, [imageAnnotation?.note]);
    return (
        <Modal
            cssClassName={CSS_CLASS_NAME}
            stopEventPropagation
            closeDialog={handleClose}
            isVisible={isVisible}
            label={modalHeading}
            type={ModalTypes.Base}>
            <BaseModalHeader heading={modalHeading} closeDialog={handleClose} />
            <section className={`${CSS_CLASS_NAME}__body`}>
                <label
                    className={`${CSS_CLASS_NAME}__note-label`}
                    htmlFor={IMAGE_ANNOTATION_NOTE_ID}>
                    {t("annotation")}
                </label>
                <AutoResizeTextArea
                    cssClassName={`${CSS_CLASS_NAME}__note-input`}
                    id={IMAGE_ANNOTATION_NOTE_ID}
                    onChange={handleOnNoteChange}
                    value={draftNote}
                    maxLength={MAX_NOTE_LENGTH}
                />
                <span className={`${CSS_CLASS_NAME}__note-input-counter`}>
                    {draftNote.length}/{MAX_NOTE_LENGTH}
                </span>
            </section>
            <BaseModalFooter
                primaryButtonProps={{
                    onClick: handleSubmitClick,
                    disabled: !draftNote,
                }}
                primaryText={t("save")}
                closeButtonProps={{ onClick: handleClose }}
                closeButtonText={t("cancel")}
                closeDialog={handleClose}
            />
        </Modal>
    );
};

export default ImageAnnotationsModal;
